import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../bizsuite-app';
import Modal from '../components/Modal';
import FormField from '../components/FormField';
import { useFormDirty } from '../hooks/useFormDirty';
import { formatDate } from '../utils/date';

const TS_STATUSES = ['draft', 'submitted', 'approved', 'rejected'];
const TASK_CATEGORIES = ['Development', 'Testing', 'Design', 'Meeting', 'Documentation', 'Support', 'Training', 'Admin', 'Other'];
const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const STATUS_BADGES = {
  draft: 'badge-gray', submitted: 'badge-yellow', approved: 'badge-green', rejected: 'badge-red'
};

export default function Timesheets() {
  const [timesheets, setTimesheets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('my'); // 'my' | 'team'
  const [filterStatus, setFilterStatus] = useState('');

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ project_id: '', week_start: '' });

  // Detail view
  const [selected, setSelected] = useState(null);
  const [entries, setEntries] = useState([]);
  const [entryModal, setEntryModal] = useState(false);
  const [entryForm, setEntryForm] = useState({ date: '', hours: '', task_category: '', description: '', billable: true });
  const [editingEntry, setEditingEntry] = useState(null);

  // Rejection
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const { dirty, markDirty, resetDirty } = useFormDirty();

  const fetchTimesheets = useCallback(async () => {
    try {
      const url = view === 'my' ? '/api/timesheets/my' : `/api/timesheets?${filterStatus ? `status=${filterStatus}` : ''}`;
      const [tsRes, pRes] = await Promise.all([
        apiFetch(url),
        apiFetch('/api/projects?limit=500')
      ]);
      if (tsRes.ok) { const d = await tsRes.json(); setTimesheets(d.timesheets || []); }
      if (pRes.ok) { const d = await pRes.json(); setProjects(d.projects || []); }
    } catch (e) { /* handled */ }
    setLoading(false);
  }, [view, filterStatus]);

  useEffect(() => { fetchTimesheets(); }, [fetchTimesheets]);

  const fmtDate = (v) => formatDate(v, '--');

  function getWeekStart(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff)).toISOString().split('T')[0];
  }

  function getWeekEnd(weekStart) {
    if (!weekStart) return '';
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 6);
    return d.toISOString().split('T')[0];
  }

  async function handleCreate() {
    if (!createForm.project_id || !createForm.week_start) return;
    const ws = getWeekStart(createForm.week_start);
    const we = getWeekEnd(ws);
    try {
      const res = await apiFetch('/api/timesheets', {
        method: 'POST',
        body: JSON.stringify({ projectId: createForm.project_id, weekStart: ws, weekEnd: we })
      });
      if (res.ok) { resetDirty(); setCreateOpen(false); setCreateForm({ project_id: '', week_start: '' }); fetchTimesheets(); }
    } catch (e) { /* handled */ }
  }

  async function fetchEntries(tsId) {
    try {
      const res = await apiFetch(`/api/timesheets/${tsId}`);
      if (res.ok) {
        const d = await res.json();
        setSelected(d.timesheet);
        setEntries(d.entries || []);
      }
    } catch (e) { /* handled */ }
  }

  function openEntryCreate() {
    setEditingEntry(null);
    setEntryForm({ date: '', hours: '', task_category: '', description: '', billable: true });
    resetDirty();
    setEntryModal(true);
  }

  function openEntryEdit(entry) {
    setEditingEntry(entry);
    setEntryForm({
      date: entry.date ? entry.date.substring(0, 10) : '',
      hours: entry.hours || '',
      task_category: entry.task_category || '',
      description: entry.description || '',
      billable: entry.billable !== false && entry.billable !== 0
    });
    resetDirty();
    setEntryModal(true);
  }

  async function saveEntry() {
    if (!entryForm.date || !entryForm.hours) return;
    const body = {
      date: entryForm.date, hours: parseFloat(entryForm.hours),
      taskCategory: entryForm.task_category || null,
      description: entryForm.description || null,
      billable: entryForm.billable
    };
    const url = editingEntry
      ? `/api/timesheets/${selected.id}/entries/${editingEntry.id}`
      : `/api/timesheets/${selected.id}/entries`;
    try {
      const res = await apiFetch(url, { method: editingEntry ? 'PUT' : 'POST', body: JSON.stringify(body) });
      if (res.ok) { resetDirty(); setEntryModal(false); fetchEntries(selected.id); }
    } catch (e) { /* handled */ }
  }

  async function deleteEntry(entryId) {
    if (!confirm('Delete this entry?')) return;
    try {
      await apiFetch(`/api/timesheets/${selected.id}/entries/${entryId}`, { method: 'DELETE' });
      fetchEntries(selected.id);
    } catch (e) { /* handled */ }
  }

  async function submitTimesheet() {
    try {
      const res = await apiFetch(`/api/timesheets/${selected.id}/submit`, { method: 'POST' });
      if (res.ok) { fetchEntries(selected.id); fetchTimesheets(); }
    } catch (e) { /* handled */ }
  }

  async function approveTimesheet() {
    try {
      const res = await apiFetch(`/api/timesheets/${selected.id}/approve`, { method: 'POST' });
      if (res.ok) { fetchEntries(selected.id); fetchTimesheets(); }
    } catch (e) { /* handled */ }
  }

  async function rejectTimesheet() {
    try {
      const res = await apiFetch(`/api/timesheets/${selected.id}/reject`, { method: 'POST', body: JSON.stringify({ reason: rejectReason }) });
      if (res.ok) { resetDirty(); setRejectModal(false); setRejectReason(''); fetchEntries(selected.id); fetchTimesheets(); }
    } catch (e) { /* handled */ }
  }

  // ---- Detail View ----
  if (selected) {
    const totalHours = entries.reduce((sum, e) => sum + (parseFloat(e.hours) || 0), 0);
    const billableHours = entries.filter(e => e.billable !== false && e.billable !== 0).reduce((sum, e) => sum + (parseFloat(e.hours) || 0), 0);

    return (
      <div>
        <button className="btn btn-secondary" style={{ marginBottom: 16 }} onClick={() => { setSelected(null); setEntries([]); }}>
          &larr; Back to Timesheets
        </button>

        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: 0 }}>{selected.project_name || 'Timesheet'}</h3>
              <span style={{ color: '#64748B' }}>Week: {fmtDate(selected.week_start)} - {fmtDate(selected.week_end)}</span>
            </div>
            <span className={`badge ${STATUS_BADGES[selected.status] || 'badge-gray'}`}>{selected.status}</span>
          </div>
          <div style={{ display: 'flex', gap: 24, marginTop: 12 }}>
            <div className="card" style={{ flex: 1, textAlign: 'center', padding: 12, background: '#F1F5F9' }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#1E293B' }}>{totalHours.toFixed(1)}</div>
              <div style={{ fontSize: 12, color: '#64748B' }}>Total Hours</div>
            </div>
            <div className="card" style={{ flex: 1, textAlign: 'center', padding: 12, background: '#F0FDF4' }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#16A34A' }}>{billableHours.toFixed(1)}</div>
              <div style={{ fontSize: 12, color: '#64748B' }}>Billable Hours</div>
            </div>
          </div>
          {selected.status === 'draft' && (
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={submitTimesheet}>Submit for Approval</button>
            </div>
          )}
          {selected.status === 'submitted' && view === 'team' && (
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={approveTimesheet}>Approve</button>
              <button className="btn btn-danger" onClick={() => { resetDirty(); setRejectModal(true); }}>Reject</button>
            </div>
          )}
          {selected.status === 'rejected' && selected.rejection_reason && (
            <div style={{ marginTop: 12, padding: 8, background: '#FEF2F2', borderRadius: 6, color: '#DC2626', fontSize: 14 }}>
              <strong>Rejection Reason:</strong> {selected.rejection_reason}
            </div>
          )}
        </div>

        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <h3>Time Entries</h3>
            {selected.status === 'draft' && (
              <button className="btn btn-primary btn-sm" onClick={openEntryCreate}>+ Add Entry</button>
            )}
          </div>
          <table className="data-table">
            <thead><tr><th>Date</th><th>Hours</th><th>Category</th><th>Description</th><th>Billable</th><th>Actions</th></tr></thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.id}>
                  <td>{fmtDate(e.date)}</td>
                  <td><strong>{parseFloat(e.hours).toFixed(1)}</strong></td>
                  <td>{e.task_category || '--'}</td>
                  <td>{e.description || '--'}</td>
                  <td>{(e.billable !== false && e.billable !== 0) ? 'Yes' : 'No'}</td>
                  <td>
                    {selected.status === 'draft' && (
                      <>
                        <button className="btn btn-secondary btn-xs" onClick={() => openEntryEdit(e)}>Edit</button>
                        <button className="btn btn-danger btn-xs" style={{ marginLeft: 4 }} onClick={() => deleteEntry(e.id)}>Del</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {entries.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: '#94A3B8', padding: 24 }}>No entries yet</td></tr>}
            </tbody>
          </table>
        </div>

        {entryModal && (
          <Modal title={editingEntry ? 'Edit Entry' : 'Add Entry'} onClose={() => setEntryModal(false)} dirty={dirty}>
            <FormField label="Date" type="date" value={entryForm.date} onChange={v => { setEntryForm(f => ({ ...f, date: v })); markDirty(); }} required />
            <FormField label="Hours" type="number" value={entryForm.hours} onChange={v => { setEntryForm(f => ({ ...f, hours: v })); markDirty(); }} required />
            <FormField label="Category" type="select" options={TASK_CATEGORIES.map(c => ({ value: c, label: c }))} value={entryForm.task_category} onChange={v => { setEntryForm(f => ({ ...f, task_category: v })); markDirty(); }} />
            <FormField label="Description" type="textarea" value={entryForm.description} onChange={v => { setEntryForm(f => ({ ...f, description: v })); markDirty(); }} />
            <div style={{ marginTop: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={entryForm.billable} onChange={e => { setEntryForm(f => ({ ...f, billable: e.target.checked })); markDirty(); }} />
                Billable
              </label>
            </div>
            <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setEntryModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEntry}>Save</button>
            </div>
          </Modal>
        )}

        {rejectModal && (
          <Modal title="Reject Timesheet" onClose={() => setRejectModal(false)} dirty={dirty}>
            <FormField label="Reason" type="textarea" value={rejectReason} onChange={v => { setRejectReason(v); markDirty(); }} required />
            <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setRejectModal(false)}>Cancel</button>
              <button className="btn btn-danger" onClick={rejectTimesheet}>Reject</button>
            </div>
          </Modal>
        )}
      </div>
    );
  }

  // ---- List View ----
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <div className="tab-bar">
            <button className={`tab-btn${view === 'my' ? ' active' : ''}`} onClick={() => setView('my')}>My Timesheets</button>
            <button className={`tab-btn${view === 'team' ? ' active' : ''}`} onClick={() => setView('team')}>Team Timesheets</button>
          </div>
          {view === 'team' && (
            <select className="form-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">All Statuses</option>
              {TS_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
        </div>
        <button className="btn btn-primary" onClick={() => { resetDirty(); setCreateOpen(true); }}>+ New Timesheet</button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" /></div>
      ) : (
        <div className="card">
          <table className="data-table">
            <thead>
              <tr>
                <th>Project</th>
                {view === 'team' && <th>Employee</th>}
                <th>Week</th>
                <th>Hours</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {timesheets.map(ts => (
                <tr key={ts.id} style={{ cursor: 'pointer' }} onClick={() => fetchEntries(ts.id)}>
                  <td><strong>{ts.project_name || '--'}</strong></td>
                  {view === 'team' && <td>{ts.employee_name || '--'}</td>}
                  <td>{fmtDate(ts.week_start)} - {fmtDate(ts.week_end)}</td>
                  <td>{parseFloat(ts.total_hours || 0).toFixed(1)}</td>
                  <td><span className={`badge ${STATUS_BADGES[ts.status] || 'badge-gray'}`}>{ts.status}</span></td>
                  <td>
                    {ts.status === 'submitted' && view === 'team' && (
                      <span className="badge badge-yellow">Pending Approval</span>
                    )}
                  </td>
                </tr>
              ))}
              {timesheets.length === 0 && (
                <tr><td colSpan={view === 'team' ? 6 : 5} style={{ textAlign: 'center', color: '#94A3B8', padding: 40 }}>No timesheets found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {createOpen && (
        <Modal title="New Timesheet" onClose={() => setCreateOpen(false)} dirty={dirty}>
          <FormField label="Project" type="select" options={projects.map(p => ({ value: p.id, label: p.name }))} value={createForm.project_id} onChange={v => { setCreateForm(f => ({ ...f, project_id: v })); markDirty(); }} required />
          <FormField label="Week Start Date" type="date" value={createForm.week_start} onChange={v => { setCreateForm(f => ({ ...f, week_start: v })); markDirty(); }} required />
          <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary" onClick={() => setCreateOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleCreate}>Create</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
